import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";

/**
 * Accessibility + UX mode context. Persists two flags in localStorage and
 * mirrors them to <html> classes so global CSS can react:
 *   - `dyslexia-on`    → switches the whole page to OpenDyslexic.
 *   - `focus-mode`     → hides side/bottom nav, credit pill, footer so the
 *                        learner sees only the current lesson.
 *
 * Kept separate from AuthContext on purpose — the user's directive forbids
 * touching AuthContext / AuthCallback / RootGate.
 */
const Ctx = createContext(null);

const LS_DYSLEXIA = "oak_dyslexia_on";
const LS_FOCUS = "oak_focus_mode_on";

function readFlag(key) {
  try { return localStorage.getItem(key) === "1"; } catch { return false; }
}
function writeFlag(key, val) {
  try { localStorage.setItem(key, val ? "1" : "0"); } catch {}
}

export function AccessibilityProvider({ children }) {
  const [dyslexia, setDyslexiaState] = useState(() => readFlag(LS_DYSLEXIA));
  const [focusMode, setFocusState] = useState(() => readFlag(LS_FOCUS));

  // Sync to <html> class on every change.
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("dyslexia-on", dyslexia);
  }, [dyslexia]);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("focus-mode", focusMode);
  }, [focusMode]);

  const setDyslexia = useCallback((v) => {
    setDyslexiaState(v);
    writeFlag(LS_DYSLEXIA, v);
  }, []);

  const setFocusMode = useCallback((v) => {
    setFocusState(v);
    writeFlag(LS_FOCUS, v);
  }, []);

  const value = useMemo(
    () => ({ dyslexia, focusMode, setDyslexia, setFocusMode }),
    [dyslexia, focusMode, setDyslexia, setFocusMode],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAccessibility() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAccessibility must be used inside <AccessibilityProvider/>");
  return v;
}
